// Resolve the in-app destination for a notification from its deep-link data.
// Mirrors the backend deep-link whitelist (src/backend/lib/notifications/
// deepLink.ts): screen ∈ "animal" | "pawmatch" | "feed". Animal links carry
// animalId + animalType; feed links may carry an animalType to pick the species
// feed, plus optional saved-search filters (age/size/gender/coatLength/color/
// energyLevel/location/kennel) applied as query params. Keep in sync with
// pushClient's tapped-notification handler.

/** Returns the route to navigate to, or null when the notification has no target. */
export function resolveNotificationPath(
  data: Record<string, string> | null | undefined,
): string | null {
  if (!data) return null;

  const screen = data.screen;

  // Animal page — either an explicit "animal" screen or legacy animalId/type.
  if (screen === "animal" || (data.animalId && data.animalType)) {
    if (!data.animalId || !data.animalType) return null;
    const segment = data.animalType === "gato" ? "gato" : "cao";
    return `/${segment}/${data.animalId}`;
  }

  if (screen === "pawmatch") return "/pawmatch";

  if (screen === "my-applications") {
    if (!data.applicationId) return "/candidaturas";
    return `/candidaturas/${data.applicationId}`;
  }

  if (screen === "feed") {
    const base = data.animalType === "gato" ? "/gatos" : "/caes";
    const filterKeys = [
      "age",
      "size",
      "gender",
      "coatLength",
      "color",
      "energyLevel",
      "location",
      "kennel",
    ] as const;
    const params = new URLSearchParams();
    for (const key of filterKeys) {
      const value = data[key];
      if (value) params.set(key, value);
    }
    if (data.lm === "any") params.set("lm", "any");
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  }

  // TODO(temporary): admin deep-link for the kennel missing-photo nudge (2026-07).
  // Degrades gracefully on older app builds (unknown screen -> null -> no CTA).
  if (screen === "admin") {
    const params = new URLSearchParams();
    if (data.tab) params.set("tab", data.tab);
    if (data.filter) params.set("filter", data.filter);
    const qs = params.toString();
    return qs ? `/painel-admin?${qs}` : "/painel-admin";
  }

  if (screen === "advertiser") {
    const params = new URLSearchParams();
    if (data.filter) params.set("filter", data.filter);
    const qs = params.toString();
    return qs ? `/meu-anuncio?${qs}` : "/meu-anuncio";
  }

  return null;
}
